import React, { useState, useContext } from "react";
import PropTypes from "prop-types";

import { debounce } from "lodash";
import Input from "components/base/Input";
import { SearchContext } from "context/providers";
import { getGroupedSkillsByCategory } from "utils/common";
import { TK_SKILL_CATEGORY_MAP } from "lookup";

const debouncedSearch = debounce(
  async (
    term,
    searchSkills,
    setIsResultsVisible,
    setResults,
    setIsSearching
  ) => {
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const data = await searchSkills(term);
      setIsResultsVisible(true);
      setResults(data || []);
    } catch (err) {
      console.error("Error searching skills:", err);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  },
  400
);

const useSkillSearch = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const { searchSkills } = useContext(SearchContext);
  const [isResultsVisible, setIsResultsVisible] = useState(false);

  const handleSearchChange = (e) => {
    const term = e.value;
    setSearchTerm(term);

    debouncedSearch(
      term,
      searchSkills,
      setIsResultsVisible,
      setResults,
      setIsSearching
    );
  };

  return {
    searchTerm,
    results,
    isSearching,
    handleSearchChange,
    isResultsVisible,
    setIsResultsVisible,
  };
};

const SearchSkillsSelector = ({
  selectSkillCallback,
  hasPermission = true,
  selectedSkills,
}) => {
  const {
    searchTerm,
    results,
    isSearching,
    handleSearchChange,
    isResultsVisible,
    setIsResultsVisible,
  } = useSkillSearch();

  const handleSkillSelect = (skill) => {
    setIsResultsVisible(false);
    selectSkillCallback(skill);
    handleSearchChange({ value: "" });
  };

  const skillsByCategory = getGroupedSkillsByCategory(
    results?.filter(
      ({ name, category }) =>
        !selectedSkills.some(
          (sk) => sk.name === name && sk.category === category
        )
    ) || []
  );

  return (
    <div>
      <div className="relative mb-2">
        <Input
          label="Search Skills"
          value={searchTerm}
          onChange={handleSearchChange}
          disabled={!hasPermission}
          placeholder="Type to search skills..."
        />
        {isSearching && (
          <div className="absolute right-3 bottom-1">
            <div className="loader !w-5 !h-5" />
          </div>
        )}
      </div>
      {isResultsVisible && Object.keys(skillsByCategory).length > 0 && (
        <div className="border rounded-md max-h-32 overflow-y-auto shadow-lg bg-white">
          {Object.keys(skillsByCategory).map((category) => (
            <div key={category}>
              <div className="sticky top-0 bg-white">
                <p className="text-md font-semibold pl-2 pt-1 text-gray-600 border-b">
                  {TK_SKILL_CATEGORY_MAP[category]}
                </p>
              </div>

              {skillsByCategory[category].map((skill) => (
                <div
                  key={skill.id}
                  className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                  onClick={() => handleSkillSelect(skill)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900">
                      {skill.name}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

SearchSkillsSelector.propTypes = {
  selectSkillCallback: PropTypes.func.isRequired,
  hasPermission: PropTypes.bool,
  selectedSkills: PropTypes.array,
};

export default SearchSkillsSelector;
